import { mockOrders } from '@/mocks/orders';
import { systemSettings } from '@/mocks/settings';
import { computeLinesTotal, round2 } from '@/lib/pricing';
import { getItemsSnapshot } from './itemsService';
import type {
  Fulfillment,
  OrderItem,
  OrderRecord,
  OrderView,
  ReviewPayload,
} from '@/types';
import { delay, shouldSimulateError } from './simulation';

const DELIVERY_DRIVER_METHODS: Fulfillment[] = [
  'self_dropoff_driver_delivery',
  'driver_pickup_driver_delivery',
];

export function needsDeliveryDriver(fulfillment: Fulfillment): boolean {
  return DELIVERY_DRIVER_METHODS.includes(fulfillment);
}

/* ------------------------------------------------------------------ *
 * In-memory working copy. This is the single source of truth shared by
 * the homepage summary and the orders pages so nothing contradicts.
 * A clearly replaceable demo layer — nothing is persisted to a backend.
 * ------------------------------------------------------------------ */
function cloneOrder(order: OrderRecord): OrderRecord {
  return {
    ...order,
    items: order.items.map((item) => ({ ...item })),
    delivery: order.delivery ? { ...order.delivery } : null,
    smartBox: order.smartBox ? { ...order.smartBox } : null,
  };
}

let store: OrderRecord[] = mockOrders.map(cloneOrder);

function buildDate(daysAgo: number): string {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toISOString();
}

/**
 * Creation time: days ago, shifted a little further back within the day so the
 * demo notification log can sit safely *after* the order it refers to.
 */
function buildCreatedAt(daysAgo: number, minutesAgo: number): string {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  date.setMinutes(date.getMinutes() - minutesAgo);
  return date.toISOString();
}

export function buildOrderView(order: OrderRecord): OrderView {
  const { createdAtDaysAgo, completedDaysAgo, createdAtMinutesAgo, delivery, ...rest } = order;

  const needsManualAssignment = Boolean(
    needsDeliveryDriver(order.fulfillment) &&
      delivery &&
      delivery.inDeliveryStage &&
      !delivery.driverAvailable &&
      delivery.customerChoice === 'wait' &&
      delivery.waitingMinutes >= systemSettings.manualAssignmentGraceMinutes &&
      !delivery.driverAccepted &&
      !order.assignedDriver,
  );

  return {
    ...rest,
    createdAt: buildCreatedAt(createdAtDaysAgo, createdAtMinutesAgo ?? 0),
    completedAt: completedDaysAgo === null ? null : buildDate(completedDaysAgo),
    needsManualAssignment,
    escalationReason: needsManualAssignment ? 'wait_grace_expired' : undefined,
  };
}

function byNewest(a: OrderView, b: OrderView): number {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

/** Synchronous snapshot for the dashboard summary derivation. */
export function getOrderViews(): OrderView[] {
  return store.map(buildOrderView);
}

function findRecord(id: string): OrderRecord | undefined {
  return store.find((order) => order.id === id);
}

function patch(id: string, changes: Partial<OrderRecord>): OrderRecord {
  store = store.map((order) => (order.id === id ? { ...order, ...changes } : order));
  const updated = findRecord(id);
  if (!updated) throw new Error('order-not-found');
  return updated;
}

const STATE_CHANGED = 'order-state-changed';

/* ------------------------------------------------------------------ *
 * Reads
 * ------------------------------------------------------------------ */

export async function fetchOrders(): Promise<OrderView[]> {
  await delay();
  if (shouldSimulateError('orders')) {
    throw new Error('failed-to-load-orders');
  }
  return getOrderViews().sort(byNewest);
}

export async function fetchOrderById(orderId: string): Promise<OrderView | null> {
  await delay(400);
  if (shouldSimulateError('order')) {
    throw new Error('failed-to-load-order');
  }
  const order = findRecord(orderId);
  return order ? buildOrderView(order) : null;
}

/* ------------------------------------------------------------------ *
 * Status transitions (FB06)
 * ------------------------------------------------------------------ */

/** Accept a new order. The system then searches for a pickup driver when needed. */
export async function acceptOrder(id: string): Promise<OrderView> {
  await delay(700);
  const order = findRecord(id);
  if (!order || order.status !== 'new') throw new Error(STATE_CHANGED);
  return buildOrderView(patch(id, { status: 'accepted' }));
}

/** Reject a new order. The system handles the customer refund. */
export async function rejectOrder(id: string): Promise<OrderView> {
  await delay(700);
  const order = findRecord(id);
  if (!order || order.status !== 'new') throw new Error(STATE_CHANGED);
  return buildOrderView(
    patch(id, { status: 'cancelled', cancelReason: 'laundry_rejected', refundStatus: 'pending' }),
  );
}

/** Confirm the clothes were physically received at the laundry. */
export async function receiveOrder(id: string): Promise<OrderView> {
  await delay(700);
  const order = findRecord(id);
  if (!order || order.status !== 'accepted') throw new Error(STATE_CHANGED);
  return buildOrderView(patch(id, { status: 'under_review', reviewAdopted: false }));
}

/**
 * Adopt the received-contents review and recompute the final amount from the
 * approved laundry prices (never invented here).
 */
export async function adoptReview(id: string, payload: ReviewPayload): Promise<OrderView> {
  await delay(800);
  const order = findRecord(id);
  if (!order || order.status !== 'under_review' || order.reviewAdopted) {
    throw new Error(STATE_CHANGED);
  }

  const itemsById = new Map(getItemsSnapshot().map((item) => [item.id, item]));
  const original = computeLinesTotal(order.items, order.priority, itemsById);
  const edited = computeLinesTotal(payload.lines, order.priority, itemsById);
  const finalTotal = round2(order.total + (edited.total - original.total));

  const nextItems: OrderItem[] = payload.lines.map((line, index) => ({
    id: line.orderItemId ?? `OI-${id}-ADD-${index + 1}`,
    itemId: line.itemId,
    itemName: line.itemName,
    serviceType: line.serviceType,
    quantity: line.quantity,
  }));

  return buildOrderView(patch(id, { items: nextItems, total: finalTotal, reviewAdopted: true }));
}

/** Start execution after the review was adopted. */
export async function startOrder(id: string): Promise<OrderView> {
  await delay(700);
  const order = findRecord(id);
  if (!order || order.status !== 'under_review' || !order.reviewAdopted) {
    throw new Error(STATE_CHANGED);
  }
  return buildOrderView(patch(id, { status: 'in_progress' }));
}

/** Mark the order ready for delivery. The system searches for a delivery driver when needed. */
export async function markReadyForDelivery(id: string): Promise<OrderView> {
  await delay(700);
  const order = findRecord(id);
  if (!order || order.status !== 'in_progress') throw new Error(STATE_CHANGED);

  const delivery = needsDeliveryDriver(order.fulfillment)
    ? {
        inDeliveryStage: true,
        driverAvailable: false,
        customerChoice: 'wait' as const,
        waitingMinutes: 0,
        driverAccepted: false,
      }
    : null;

  return buildOrderView(patch(id, { status: 'ready_for_delivery', delivery }));
}

/**
 * Manually assign an available sub-driver to an escalated delivery task.
 * The assignment is mandatory — no driver accept/reject step.
 */
export async function assignDeliveryDriver(
  id: string,
  _driverId: string,
  driverName: string,
): Promise<OrderView> {
  await delay(700);
  const order = findRecord(id);
  if (
    !order ||
    !order.delivery ||
    !order.delivery.inDeliveryStage ||
    order.delivery.driverAccepted ||
    order.assignedDriver
  ) {
    throw new Error('task-unavailable');
  }
  return buildOrderView(
    patch(id, {
      assignedDriver: driverName,
      delivery: { ...order.delivery, driverAccepted: true, driverAvailable: true },
    }),
  );
}